import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Shield, Clock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const DataDeletion = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <nav className="navbar px-6 py-4 bg-white/80 backdrop-blur-xl border-b sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.png" alt="AutomateSEO.ai" className="h-11 w-auto" />
            <span className="text-2xl font-bold heading bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AutomateSEO.ai</span>
          </div>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back to Home
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">User Data Deletion Policy</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 28, 2025</p>
        
        <div className="prose prose-lg max-w-none space-y-8">
          <section className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500">
            <div className="flex items-start space-x-4">
              <Shield className="h-8 w-8 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold mb-4 text-gray-900">Your Privacy Rights</h2>
                <p className="text-gray-700 leading-relaxed">
                  AutomateSEO.ai respects user privacy and data protection rights. We are committed to ensuring that you have full control over your personal data collected through our platform and integrated services.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">How to Request Data Deletion</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              If you want to delete your personal data collected through Facebook, Instagram, or WhatsApp integrations, you can request deletion by emailing us at:
            </p>
            
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-lg mb-6">
              <div className="flex items-center space-x-3">
                <Mail className="h-6 w-6" />
                <a href="mailto:support@automateseo.ai" className="text-xl font-semibold hover:underline">
                  support@automateseo.ai
                </a>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Information to Include in Your Request</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To help us process your request quickly and accurately, please include the following information in your email:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-gray-900">Your Name</strong>
                    <p className="text-gray-600">The full name associated with your account</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-gray-900">Platform Used</strong>
                    <p className="text-gray-600">Specify the platform: Facebook, Instagram, or WhatsApp</p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="text-gray-900">Registered Email ID</strong>
                    <p className="text-gray-600">The email address you used to register with AutomateSEO.ai</p>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Processing Timeline</h2>
            <div className="flex items-start space-x-4 bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
              <Clock className="h-8 w-8 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <p className="text-gray-700 leading-relaxed">
                  Once we receive your request, we will verify your identity and <strong>permanently delete your data within 7 working days</strong>. You will receive a confirmation email once the deletion process is complete.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Questions?</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions regarding data deletion or need assistance with your request, please contact us at the same email address:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> <a href="mailto:support@automateseo.ai" className="text-blue-600 hover:underline">support@automateseo.ai</a>
            </p>
          </section>

          <section className="bg-purple-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-gray-900">Related Policies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For more information about how we handle your data, please review our other policies:
            </p>
            <ul className="space-y-2">
              <li>
                <a href="/privacy" className="text-blue-600 hover:underline flex items-center space-x-2">
                  <span>→</span>
                  <span>Privacy Policy</span>
                </a>
              </li>
              <li>
                <a href="/terms" className="text-blue-600 hover:underline flex items-center space-x-2">
                  <span>→</span>
                  <span>Terms of Service</span>
                </a>
              </li>
              <li>
                <a href="/cookie-policy" className="text-blue-600 hover:underline flex items-center space-x-2">
                  <span>→</span>
                  <span>Cookie Policy</span>
                </a>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DataDeletion;
